import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { X, Camera, Video, Sparkles, Loader2, Trash2, Plus } from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

const MOODS = [
  { key: "happy", emoji: "😊", label: "Happy" },
  { key: "calm", emoji: "😌", label: "Calm" },
  { key: "grateful", emoji: "🥰", label: "Grateful" },
  { key: "hopeful", emoji: "🥺", label: "Hopeful" },
  { key: "tired", emoji: "😴", label: "Tired" },
  { key: "sad", emoji: "😔", label: "Sad" },
  { key: "anxious", emoji: "😣", label: "Anxious" },
  { key: "frustrated", emoji: "😤", label: "Frustrated" },
];

const EMOTION_TAGS = ["gentle", "resilient", "achy", "swollen", "restless", "supported", "overwhelmed", "peaceful", "sore", "energized"];

const SELF_CARE = [
  { key: "compression", label: "Compression", emoji: "🧦" },
  { key: "massage", label: "MLD Massage", emoji: "🤲" },
  { key: "elevation", label: "Elevation", emoji: "🛋️" },
  { key: "bandaging", label: "Bandaging", emoji: "🎀" },
  { key: "exercise", label: "Exercise", emoji: "🧘" },
  { key: "skincare", label: "Skincare", emoji: "🧴" },
];

const emptyEntry = (date) => ({
  date, journal: "", mood_emoji: null, mood_intensity: 3, emotion_tags: [],
  self_care: {}, medication_taken: false, medication_notes: "",
  flare_up: false, pain_level: 0, swelling_level: 0, photos: [], video: null,
});

export default function DayModal({ dateKey, initialEntry, onClose, onSaved }) {
  const [entry, setEntry] = useState(initialEntry || emptyEntry(dateKey));
  const [saving, setSaving] = useState(false);
  const [summarizing, setSummarizing] = useState(false);
  const [photoUrls, setPhotoUrls] = useState({});
  const [videoUrl, setVideoUrl] = useState(null);
  const [customTag, setCustomTag] = useState("");
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);

  useEffect(() => {
    // Load photo blobs
    const token = localStorage.getItem("lc_token");
    if (entry.photos?.length) {
      entry.photos.forEach(async (p) => {
        if (photoUrls[p]) return;
        try {
          const res = await fetch(`${API}/files/${p}?auth=${token}`);
          const blob = await res.blob();
          setPhotoUrls((prev) => ({ ...prev, [p]: URL.createObjectURL(blob) }));
        } catch {}
      });
    }
    if (entry.video && !videoUrl) {
      (async () => {
        try {
          const res = await fetch(`${API}/files/${entry.video}?auth=${token}`);
          const blob = await res.blob();
          setVideoUrl(URL.createObjectURL(blob));
        } catch {}
      })();
    }
    // eslint-disable-next-line
  }, [entry.photos, entry.video]);

  const update = (patch) => setEntry((e) => ({ ...e, ...patch }));
  const toggleSelfCare = (k) => update({ self_care: { ...entry.self_care, [k]: !entry.self_care?.[k] } });
  const toggleTag = (t) => {
    const has = entry.emotion_tags.includes(t);
    update({ emotion_tags: has ? entry.emotion_tags.filter((x) => x !== t) : [...entry.emotion_tags, t] });
  };

  const handleUpload = async (file, kind) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      if (kind === "photo") update({ photos: [...(entry.photos || []), data.path] });
      else update({ video: data.path });
      toast.success(kind === "photo" ? "Photo added ♡" : "Video added ♡");
    } catch (e) {
      toast.error("Upload failed");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/entries", entry);
      setEntry(data);
      onSaved(data);
      return data;
    } finally {
      setSaving(false);
    }
  };

  const closeWithSummary = async () => {
    // Only save + summarize if there's actually something logged
    const hasContent = entry.journal?.trim() || entry.mood_emoji || entry.emotion_tags.length ||
                       Object.values(entry.self_care || {}).some(Boolean) || entry.flare_up ||
                       entry.pain_level > 0 || entry.swelling_level > 0 || entry.medication_taken;
    if (!hasContent) { onClose(); return; }

    const saved = await save();
    setSummarizing(true);
    try {
      const { data } = await api.post(`/entries/${dateKey}/summarize`);
      const updated = { ...saved, summary: data.summary };
      onSaved(updated);
      toast.success("Your reflection is ready ♡");
    } catch (e) {
      toast.error("Couldn't generate reflection, but your entry is saved");
    } finally {
      setSummarizing(false);
      onClose();
    }
  };

  const removePhoto = (path) => update({ photos: entry.photos.filter((p) => p !== path) });
  const removeVideo = () => update({ video: null });

  const displayDate = format(parseISO(dateKey), "EEEE, MMMM d");
  const isPast = dateKey < format(new Date(), "yyyy-MM-dd");

  const openPastCellulitis = () => {
    window.dispatchEvent(new CustomEvent("open-cellulitis-past", { detail: { date: dateKey } }));
    onClose();
  };

  return (
    <motion.div
      className="fixed inset-0 z-50 backdrop-blur-xl bg-white/40 overflow-y-auto"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        layoutId={`day-${dateKey}`}
        onClick={(e) => e.stopPropagation()}
        className="max-w-4xl mx-auto my-6 md:my-12 kawaii-card p-6 md:p-10"
        data-testid="day-modal"
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-kawaii-textSoft font-semibold">Daily entry</p>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-kawaii-text mt-1">{displayDate}</h2>
            {entry.summary && (
              <div className="mt-4 p-4 rounded-2xl bg-kawaii-lavender/25 border border-kawaii-lavender/40 max-w-xl">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="w-3.5 h-3.5 text-kawaii-textSoft" strokeWidth={2.5} />
                  <span className="text-[10px] uppercase tracking-[0.2em] text-kawaii-textSoft font-semibold">Your reflection</span>
                </div>
                <p className="text-sm text-kawaii-text font-body leading-relaxed" data-testid="ai-summary">{entry.summary}</p>
              </div>
            )}
          </div>
          <button
            onClick={closeWithSummary}
            disabled={summarizing || saving}
            data-testid="close-modal-btn"
            className="kawaii-btn w-10 h-10 rounded-full bg-white border border-kawaii-border flex items-center justify-center disabled:opacity-50"
          >
            {summarizing ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-5 h-5" strokeWidth={2.5} />}
          </button>
        </div>

        {isPast && (
          <button
            type="button"
            onClick={openPastCellulitis}
            data-testid="log-past-cellulitis-btn"
            className="kawaii-btn mb-5 px-3 py-1.5 rounded-full bg-white border border-[#E63946]/40 text-[#E63946] text-xs font-body font-semibold inline-flex items-center gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" strokeWidth={2.5} /> Log a past cellulitis for this day
          </button>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          {/* Journal */}
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-[0.2em] text-kawaii-textSoft font-semibold">Journal</label>
            <Textarea
              data-testid="journal-input"
              value={entry.journal}
              onChange={(e) => update({ journal: e.target.value })}
              placeholder="How are you feeling today? What did you notice about your body, your day, your heart?"
              className="mt-2 min-h-32 rounded-2xl border-kawaii-border bg-white/70 font-body text-kawaii-text placeholder:text-kawaii-textSoft/50"
            />
          </div>

          {/* Mood */}
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-kawaii-textSoft font-semibold">Mood</label>
            <div className="mt-3 flex flex-wrap gap-2">
              {MOODS.map((m) => (
                <button
                  key={m.key}
                  data-testid={`mood-${m.key}`}
                  onClick={() => update({ mood_emoji: entry.mood_emoji === m.key ? null : m.key })}
                  className={`kawaii-btn w-12 h-12 rounded-2xl border text-2xl ${entry.mood_emoji === m.key ? 'bg-kawaii-pink/30 border-kawaii-pink scale-110' : 'bg-white border-kawaii-border'}`}
                  title={m.label}
                >
                  {m.emoji}
                </button>
              ))}
            </div>
            <div className="mt-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-kawaii-textSoft font-body">Intensity</span>
                <span className="text-sm text-kawaii-text font-semibold">{entry.mood_intensity}/5</span>
              </div>
              <Slider
                value={[entry.mood_intensity]}
                onValueChange={([v]) => update({ mood_intensity: v })}
                min={1} max={5} step={1}
                data-testid="mood-intensity"
              />
            </div>
          </div>

          {/* Emotion tags */}
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-kawaii-textSoft font-semibold">Feelings</label>
            <div className="mt-3 flex flex-wrap gap-2">
              {EMOTION_TAGS.map((t) => (
                <button
                  key={t}
                  data-testid={`tag-${t}`}
                  onClick={() => toggleTag(t)}
                  className={`kawaii-btn px-3.5 py-1.5 rounded-full text-sm font-body font-semibold border ${entry.emotion_tags.includes(t) ? 'bg-kawaii-lavender/50 border-kawaii-lavender text-kawaii-text' : 'bg-white border-kawaii-border text-kawaii-textSoft'}`}
                >
                  {t}
                </button>
              ))}
              {/* Custom tags */}
              {entry.emotion_tags.filter((t) => !EMOTION_TAGS.includes(t)).map((t) => (
                <button
                  key={t}
                  data-testid={`custom-tag-${t}`}
                  onClick={() => toggleTag(t)}
                  className="kawaii-btn px-3.5 py-1.5 rounded-full text-sm font-body font-semibold border bg-kawaii-pink/30 border-kawaii-pink text-kawaii-text inline-flex items-center gap-1"
                  title="Click to remove"
                >
                  {t} <X className="w-3 h-3" strokeWidth={2.5} />
                </button>
              ))}
              {/* Add custom tag input */}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const v = customTag.trim().toLowerCase();
                  if (!v) return;
                  if (v.length > 24) return toast.error("Keep tags under 24 chars");
                  if (!entry.emotion_tags.includes(v)) {
                    update({ emotion_tags: [...entry.emotion_tags, v] });
                  }
                  setCustomTag("");
                }}
                className="inline-flex items-center gap-1"
              >
                <input
                  data-testid="custom-tag-input"
                  value={customTag}
                  onChange={(e) => setCustomTag(e.target.value)}
                  placeholder="add your own..."
                  className="px-3 py-1.5 rounded-full text-sm font-body border border-dashed border-kawaii-lavender/60 bg-white/60 focus:outline-none focus:ring-2 focus:ring-kawaii-pink w-32"
                />
                <button
                  type="submit"
                  data-testid="add-custom-tag"
                  className="kawaii-btn w-8 h-8 rounded-full bg-kawaii-pink text-white flex items-center justify-center shadow-sm"
                  aria-label="Add feeling"
                >
                  <Plus className="w-4 h-4" strokeWidth={2.8} />
                </button>
              </form>
            </div>
          </div>

          {/* Self-care */}
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-[0.2em] text-kawaii-textSoft font-semibold">Self-care today</label>
            <div className="mt-3 flex flex-wrap gap-2">
              {SELF_CARE.map((s) => {
                const active = !!entry.self_care?.[s.key];
                return (
                  <button
                    key={s.key}
                    data-testid={`selfcare-${s.key}`}
                    onClick={() => toggleSelfCare(s.key)}
                    className={`kawaii-btn px-4 py-2 rounded-full text-sm font-body font-semibold border inline-flex items-center gap-2 ${active ? 'bg-kawaii-mint/60 border-kawaii-mint text-kawaii-text' : 'bg-white border-kawaii-border text-kawaii-textSoft'}`}
                  >
                    <span className="text-base">{s.emoji}</span> {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Pain */}
          <div className="p-5 rounded-2xl bg-kawaii-peach/20 border border-kawaii-peach/40">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-[0.2em] text-kawaii-textSoft font-semibold">Pain level</span>
              <span className="font-heading text-2xl font-semibold text-kawaii-text">{entry.pain_level}/10</span>
            </div>
            <Slider
              className="mt-3"
              value={[entry.pain_level]}
              onValueChange={([v]) => update({ pain_level: v })}
              min={0} max={10} step={1}
              data-testid="pain-slider"
            />
          </div>

          {/* Swelling */}
          <div className="p-5 rounded-2xl bg-kawaii-lavender/20 border border-kawaii-lavender/40">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-[0.2em] text-kawaii-textSoft font-semibold">Swelling level</span>
              <span className="font-heading text-2xl font-semibold text-kawaii-text">{entry.swelling_level}/10</span>
            </div>
            <Slider
              className="mt-3"
              value={[entry.swelling_level]}
              onValueChange={([v]) => update({ swelling_level: v })}
              min={0} max={10} step={1}
              data-testid="swelling-slider"
            />
          </div>

          {/* Flare-up & Medication */}
          <div className="p-5 rounded-2xl bg-white border border-kawaii-border flex items-center justify-between">
            <div>
              <div className="font-heading font-semibold text-kawaii-text">Flare-up today?</div>
              <div className="text-xs text-kawaii-textSoft mt-0.5">Tracking helps spot patterns</div>
            </div>
            <Switch
              checked={entry.flare_up}
              onCheckedChange={(v) => update({ flare_up: v })}
              data-testid="flare-toggle"
            />
          </div>
          <div className="p-5 rounded-2xl bg-white border border-kawaii-border">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-heading font-semibold text-kawaii-text">Medication taken</div>
                <div className="text-xs text-kawaii-textSoft mt-0.5">Add any notes below</div>
              </div>
              <Switch
                checked={entry.medication_taken}
                onCheckedChange={(v) => update({ medication_taken: v })}
                data-testid="med-toggle"
              />
            </div>
            {entry.medication_taken && (
              <input
                data-testid="med-notes"
                value={entry.medication_notes}
                onChange={(e) => update({ medication_notes: e.target.value })}
                placeholder="Dose, name, or note..."
                className="mt-3 w-full px-3 py-2 rounded-xl border border-kawaii-border bg-white/70 text-sm font-body focus:outline-none focus:ring-2 focus:ring-kawaii-pink"
              />
            )}
          </div>

          {/* Photos */}
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-[0.2em] text-kawaii-textSoft font-semibold">Photos & video log</label>
            <div className="mt-3 flex flex-wrap gap-3">
              {(entry.photos || []).map((p) => (
                <div key={p} className="relative w-24 h-24 rounded-2xl overflow-hidden border border-kawaii-border bg-kawaii-border/30">
                  {photoUrls[p] ? (
                    <img src={photoUrls[p]} alt="log" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-kawaii-textSoft"><Loader2 className="w-4 h-4 animate-spin" /></div>
                  )}
                  <button
                    onClick={() => removePhoto(p)}
                    className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/90 flex items-center justify-center shadow-sm"
                  >
                    <Trash2 className="w-3 h-3 text-kawaii-text" />
                  </button>
                </div>
              ))}
              <button
                onClick={() => photoInputRef.current?.click()}
                data-testid="add-photo-btn"
                className="kawaii-btn w-24 h-24 rounded-2xl border-2 border-dashed border-kawaii-pink/50 bg-kawaii-pink/10 flex flex-col items-center justify-center text-kawaii-textSoft"
              >
                <Camera className="w-5 h-5" strokeWidth={2.5} />
                <span className="text-[10px] mt-1 font-semibold uppercase tracking-wider">Photo</span>
              </button>
              <input
                ref={photoInputRef} type="file" accept="image/*" hidden
                onChange={(e) => { handleUpload(e.target.files?.[0], "photo"); e.target.value = ""; }}
              />
              {entry.video ? (
                <div className="relative w-40 h-24 rounded-2xl overflow-hidden border border-kawaii-border bg-black">
                  {videoUrl ? (
                    <video src={videoUrl} controls className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white"><Loader2 className="w-4 h-4 animate-spin" /></div>
                  )}
                  <button
                    onClick={removeVideo}
                    className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/90 flex items-center justify-center shadow-sm"
                  >
                    <Trash2 className="w-3 h-3 text-kawaii-text" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => videoInputRef.current?.click()}
                  data-testid="add-video-btn"
                  className="kawaii-btn w-24 h-24 rounded-2xl border-2 border-dashed border-kawaii-lavender/60 bg-kawaii-lavender/10 flex flex-col items-center justify-center text-kawaii-textSoft"
                >
                  <Video className="w-5 h-5" strokeWidth={2.5} />
                  <span className="text-[10px] mt-1 font-semibold uppercase tracking-wider">Video</span>
                </button>
              )}
              <input
                ref={videoInputRef} type="file" accept="video/*" hidden
                onChange={(e) => { handleUpload(e.target.files?.[0], "video"); e.target.value = ""; }}
              />
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
          <p className="text-xs text-kawaii-textSoft font-body italic">
            Closing this entry will save it and gently reflect on your day ♡
          </p>
          <div className="flex gap-2">
            <button
              onClick={save}
              disabled={saving}
              data-testid="save-only-btn"
              className="kawaii-btn px-5 py-2.5 rounded-full bg-white border border-kawaii-border font-body font-semibold text-sm text-kawaii-text disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save"}
            </button>
            <button
              onClick={closeWithSummary}
              disabled={summarizing || saving}
              data-testid="save-and-close-btn"
              className="kawaii-btn px-5 py-2.5 rounded-full bg-gradient-to-r from-kawaii-pink to-kawaii-peach text-white font-body font-semibold text-sm shadow-md disabled:opacity-60 inline-flex items-center gap-2"
            >
              {summarizing ? <><Loader2 className="w-4 h-4 animate-spin" /> Reflecting...</> : <><Sparkles className="w-4 h-4" strokeWidth={2.5} /> Save & reflect</>}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
